import {
  ApolloClient,
  ApolloLink,
  ApolloCache,
  HttpLink,
  InMemoryCache,
  InMemoryCacheConfig,
  NormalizedCacheObject,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import {
  ErrorHandler,
  ErrorResponse,
  onError,
} from '@apollo/client/link/error';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { sha256 } from 'crypto-hash';
import fetch from 'isomorphic-fetch';
import { Env, getProcessEnvironmentValue } from '@gif/shared-global';

const environment = (getProcessEnvironmentValue('APP_ENV') || 'prod') as Env;

export type CreateApolloCacheOptions = Pick<
  CreateApolloClientOptions<NormalizedCacheObject>,
  'cacheData' | 'configOptions' | 'isBrowser'
>;

export type CreateApolloClientOptions<TCacheShape> = {
  appName?: string;
  appVersion?: string;
  cache?: ApolloCache<TCacheShape>;
  cacheData?: TCacheShape;
  configOptions?: InMemoryCacheConfig;
  disablePersistedQueries?: boolean;
  env?: Env;
  isBrowser?: boolean;
  link?: ApolloLink;
  onErrorHandler?: ErrorHandler;
  url: string;
};

export type CreateApolloClientLinkOptions = Pick<
  CreateApolloClientOptions<NormalizedCacheObject>,
  'disablePersistedQueries' | 'isBrowser' | 'onErrorHandler' | 'url'
>;

export function createApolloCache({
  cacheData,
  configOptions,
  isBrowser = true,
}: CreateApolloCacheOptions) {
  if (isBrowser && cacheData) {
    return new InMemoryCache(configOptions).restore(cacheData);
  }
  return new InMemoryCache(configOptions);
}

export function createApolloClient({
  appName,
  appVersion,
  cache,
  cacheData,
  configOptions,
  disablePersistedQueries,
  isBrowser = true,
  link,
  onErrorHandler,
  url,
}: CreateApolloClientOptions<NormalizedCacheObject>) {
  const localCache = cache
    ? cache
    : createApolloCache({ cacheData, configOptions, isBrowser });
  const localLink = link
    ? link
    : createApolloClientLink({
        disablePersistedQueries,
        isBrowser,
        onErrorHandler,
        url,
      });
  const client = new ApolloClient({
    ssrMode: !isBrowser,
    link: localLink,
    cache: localCache,
    name: appName,
    // Only turn on the connectToDevTools for non production env
    connectToDevTools: environment !== 'prod',
    version: appVersion,
  });
  return { cache: localCache, client };
}

export function createApolloClientLink({
  disablePersistedQueries,
  isBrowser,
  onErrorHandler,
  url,
}: CreateApolloClientLinkOptions) {
  const links = [];
  if (!disablePersistedQueries) {
    links.push(
      createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: true,
      })
    );
  }
  links.push(
    onError(
      onErrorHandler ??
        (({
          graphQLErrors,
          networkError,
          operation: { operationName },
        }: ErrorResponse) => {
          if (graphQLErrors) {
            graphQLErrors.forEach(({ message }) =>
              console.error(
                `[GraphQL Error > Operation: ${operationName}] -- ${message}`
              )
            );
          }
          if (networkError) {
            console.error(
              `Network Error > Operation: ${operationName}] -- ${networkError.message}`
            );
          }
        })
    )
  );
  const httpLinkOptions: HttpLink['options'] = { uri: url };
  if (!isBrowser) {
    httpLinkOptions.fetch = fetch;
  } else {
    const authLink = setContext((_, { headers }) => {
      const token =
        localStorage.getItem('access_token') ??
        (window?.mlbOkta?.getAuthClient()?.tokenManager as any)?.get(
          'access_token'
        )?.accessToken;
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
        },
      };
    });
    links.push(authLink);
  }
  const httpLink = new HttpLink(httpLinkOptions);
  links.push(httpLink);
  return ApolloLink.from(links);
}
